import { useState, useMemo, useContext, useCallback, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useLocation } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { DatePicker, Tooltip } from "antd"
import { InformationCircleIcon } from "@heroicons/react/outline"

import { UserContext } from "../../../hooks/UserContext"
import {
    NewTable,
    PageTopBar,
    PageView,
    Notification,
    Loader,
    SelectInput,
    Checkbox,
    Button,
    CarrierName,
} from "../../../components"
import { StatusPill } from "../../../components/BasicTable/StatusPill"
import { setAutomatedCoverageDeactivationsPageSize } from "../../../redux/pageSizeSlice"
import {
    fetchAutomatedShippingCoverageDeactivations,
    acknowledgeAutomatedShippingCoverageDeactivation,
} from "../../../services"
import { DeactivationDetailDrawer } from "./DeactivationDetailDrawer"
import { buildQueryParams } from "./utils"

const { RangePicker } = DatePicker
const T_PREFIX = "lastmile_management.automated_coverage_deactivations"
const PERMISSION_KEY = "manage_automated_shipping_coverage_deactivations"

const STATUS_DEFS = (i18n) => [
    {
        status: "pending",
        label: i18n.t(`${T_PREFIX}.status.pending`),
        classes: "bg-yellow-100 text-yellow-800 border-yellow-200",
    },
    {
        status: "acknowledged",
        label: i18n.t(`${T_PREFIX}.status.acknowledged`),
        classes: "bg-green-100 text-green-800 border-green-200",
    },
]

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s)

// Renders a synced horizontal scrollbar above any child that has its own
// internal scrollable region. NewTable wraps its <table> in `overflow-x-auto`,
// so we find that descendant at runtime and mirror its scrollLeft.
const DoubleScroll = ({ children }) => {
    const topRef = useRef(null)
    const wrapperRef = useRef(null)
    const fillerRef = useRef(null)
    const isSyncing = useRef(false)

    useEffect(() => {
        const top = topRef.current
        const wrapper = wrapperRef.current
        const filler = fillerRef.current
        if (!top || !wrapper || !filler) return

        let scroller = null
        let table = null
        let onScrollerHandler = null

        const findScroller = () => {
            // Look for an element with overflow-x: auto/scroll. NewTable's
            // wrapper has `overflow-x-auto` plus a <table> inside. Skip the
            // top scrollbar itself (which also has overflow-x-auto).
            const candidates = wrapper.querySelectorAll("*")
            for (const el of candidates) {
                if (el === top || top.contains(el)) continue
                const style = window.getComputedStyle(el)
                if (style.overflowX === "auto" || style.overflowX === "scroll") {
                    return el
                }
            }
            return null
        }

        const onTop = () => {
            if (!scroller) return
            if (isSyncing.current) { isSyncing.current = false; return }
            isSyncing.current = true
            scroller.scrollLeft = top.scrollLeft
        }

        const wireScroller = () => {
            if (onScrollerHandler && scroller) {
                scroller.removeEventListener("scroll", onScrollerHandler)
            }
            scroller = findScroller()
            table = scroller?.querySelector("table") || null
            if (scroller) {
                onScrollerHandler = () => {
                    if (isSyncing.current) { isSyncing.current = false; return }
                    isSyncing.current = true
                    top.scrollLeft = scroller.scrollLeft
                }
                scroller.addEventListener("scroll", onScrollerHandler)
            }
        }

        const updateWidth = () => {
            if (!scroller) wireScroller()
            const widthSource = table || scroller
            if (widthSource) {
                filler.style.width = `${widthSource.scrollWidth}px`
            }
        }

        wireScroller()
        updateWidth()
        top.addEventListener("scroll", onTop)

        const ro = new ResizeObserver(() => {
            // Re-discover the scroller in case NewTable re-rendered.
            if (!scroller || !wrapper.contains(scroller)) {
                wireScroller()
            }
            updateWidth()
        })
        ro.observe(wrapper)

        // Also observe DOM mutations so we update when NewTable swaps its inner subtree.
        const mo = new MutationObserver(() => {
            if (!scroller || !wrapper.contains(scroller)) {
                wireScroller()
            }
            updateWidth()
        })
        mo.observe(wrapper, { childList: true, subtree: true })

        return () => {
            ro.disconnect()
            mo.disconnect()
            top.removeEventListener("scroll", onTop)
            if (onScrollerHandler && scroller) {
                scroller.removeEventListener("scroll", onScrollerHandler)
            }
        }
    }, [children])

    return (
        <div ref={wrapperRef}>
            <div
                ref={topRef}
                className="overflow-x-auto"
                style={{ height: 16, marginBottom: 4 }}
            >
                <div ref={fillerRef} style={{ height: 1, minWidth: "100%" }} />
            </div>
            {children}
        </div>
    )
}

const formatDate = (iso) => {
    if (!iso) return "—"
    try {
        return new Date(iso).toLocaleString()
    } catch (e) {
        return iso
    }
}

const formatDateParts = (iso) => {
    if (!iso) return { date: "—", time: "" }
    try {
        const d = new Date(iso)
        return {
            date: d.toLocaleDateString(),
            time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        }
    } catch (e) {
        return { date: iso, time: "" }
    }
}

export const ListAutomatedCoverageDeactivationsContainer = () => {
    const { i18n } = useTranslation()
    const { user } = useContext(UserContext)
    const { warehouse_id } = useParams()
    const location = useLocation()
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const pageSize = useSelector((state) => state.pageSize.automatedCoverageDeactivationsList)

    const hasAccess = !!user?.permissions?.[PERMISSION_KEY]

    const [filters, setFilters] = useState({
        onlyPending: true,
        carrier: null,
        dateFrom: null,
        dateTo: null,
    })
    const [page, setPage] = useState(1)
    const [selectedSnapshot, setSelectedSnapshot] = useState(null)
    const [notification, setNotification] = useState({ show: false, type: "", title: "" })

    const showNotification = (type, title) => {
        setNotification({ show: true, type, title })
        setTimeout(() => setNotification({ show: false, type: "", title: "" }), 3000)
    }

    const queryParams = useMemo(
        () => buildQueryParams(filters, page, parseInt(pageSize, 10) || 25, warehouse_id),
        [filters, page, pageSize, warehouse_id]
    )

    const { data, isLoading, isError, error, isFetching, isPreviousData, refetch } = useQuery(
        ["automated-coverage-deactivations", queryParams],
        () => fetchAutomatedShippingCoverageDeactivations(queryParams),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            enabled: hasAccess,
        }
    )

    const acknowledgeMutation = useMutation(
        (id) => acknowledgeAutomatedShippingCoverageDeactivation(id),
        {
            onSuccess: (updated) => {
                queryClient.invalidateQueries(["automated-coverage-deactivations"])
                if (selectedSnapshot && updated && selectedSnapshot.id === updated.id) {
                    setSelectedSnapshot((prev) => ({ ...prev, ...updated }))
                }
                showNotification("success", i18n.t(`${T_PREFIX}.toast.acknowledgeSuccess`))
            },
            onError: (err) => {
                if (err?.response?.status === 404) {
                    showNotification("error", i18n.t(`${T_PREFIX}.toast.notFoundError`))
                    queryClient.invalidateQueries(["automated-coverage-deactivations"])
                    return
                }
                showNotification("error", i18n.t(`${T_PREFIX}.toast.acknowledgeError`))
            },
        }
    )

    const items = data?.automated_shipping_coverage_deactivations || []
    const meta = data?.meta || {}

    const carrierOptions = useMemo(() => {
        const set = new Set()
        items.forEach((d) => {
            if (d.shipping_method?.carrier_name) set.add(d.shipping_method.carrier_name)
        })
        const all = { id: "__all__", name: i18n.t(`${T_PREFIX}.filters.allCarriers`) }
        const opts = Array.from(set).map((c) => ({ id: c, name: c }))
        return [all, ...opts]
    }, [items, i18n])

    const selectedCarrierOption = useMemo(() => {
        if (!filters.carrier) return carrierOptions[0]
        return (
            carrierOptions.find((o) => o.id === filters.carrier) || {
                id: filters.carrier,
                name: filters.carrier,
            }
        )
    }, [filters.carrier, carrierOptions])

    const handleFilterChange = useCallback((updates) => {
        setFilters((prev) => ({ ...prev, ...updates }))
        setPage(1)
    }, [])

    const handlePageSizeChange = (option) => {
        dispatch(setAutomatedCoverageDeactivationsPageSize(option.id))
        setPage(1)
    }

    const handlePaginationChange = (newPage) => {
        setPage(newPage)
    }

    const handleRowClick = (deactivation) => {
        setSelectedSnapshot(deactivation)
    }

    const handleAcknowledge = (id) => {
        acknowledgeMutation.mutate(id)
    }

    const handleDateRangeChange = (range) => {
        if (!range || range.length === 0) {
            handleFilterChange({ dateFrom: null, dateTo: null })
            return
        }
        const [from, to] = range
        handleFilterChange({
            dateFrom: from ? from.startOf("day") : null,
            dateTo: to ? to.endOf("day") : null,
        })
    }

    const dateRangeValue = useMemo(() => {
        if (filters.dateFrom && filters.dateTo) return [filters.dateFrom, filters.dateTo]
        return null
    }, [filters.dateFrom, filters.dateTo])

    // Build table rows
    const tableData = useMemo(() => {
        return items.map((d) => {
            const isAcknowledged = !!d.acknowledged_at
            const sm = d.shipping_method
            const sc = d.shipping_coverage
            const fe = d.fulfillment_error

            return {
                key: d.id,
                object: d,
                createdAt: (() => {
                    const parts = formatDateParts(d.created_at)
                    return (
                        <div className="text-sm whitespace-nowrap" style={{ minWidth: 110 }}>
                            <div className="text-gray-900">{parts.date}</div>
                            {parts.time && <div className="text-gray-500 text-xs">{parts.time}</div>}
                        </div>
                    )
                })(),
                carrier: (
                    <div className="text-sm text-gray-900 whitespace-nowrap" style={{ minWidth: 100 }}>
                        <CarrierName preferInternal carrierName={sm?.carrier_name} internalCarrierName={sm?.internal_carrier_name} placeholder="—" />
                    </div>
                ),
                shippingMethod: (
                    <div className="text-sm text-gray-700 whitespace-nowrap" style={{ minWidth: 120 }}>
                        {sm?.shipping_name || "—"}
                    </div>
                ),
                zipCode: (
                    <div className="text-sm text-gray-700 whitespace-nowrap" style={{ minWidth: 80 }}>
                        {sc?.value || "—"}
                    </div>
                ),
                order: (
                    <div className="text-sm text-gray-700 whitespace-nowrap" style={{ minWidth: 80 }}>
                        {fe?.order_id ? (
                            <a
                                href={`/warehouse/${warehouse_id}/orders/show/${fe.order_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                #{fe.order_id}
                            </a>
                        ) : (
                            i18n.t(`${T_PREFIX}.drawer.noOrder`)
                        )}
                    </div>
                ),
                message: (
                    <div
                        className="text-sm text-gray-500 whitespace-nowrap"
                        title={fe?.error_message || ""}
                        style={{ minWidth: 280 }}
                    >
                        {truncate(fe?.error_message, 60) || "—"}
                    </div>
                ),
                status: (
                    <StatusPill
                        status={isAcknowledged ? "acknowledged" : "pending"}
                        statuses={STATUS_DEFS(i18n)}
                    />
                ),
                actions: !isAcknowledged ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            handleAcknowledge(d.id)
                        }}
                        disabled={acknowledgeMutation.isLoading}
                        className="px-3 py-1 text-sm font-medium text-indigo-600 border border-indigo-200 rounded hover:bg-indigo-50 disabled:opacity-50"
                    >
                        {i18n.t(`${T_PREFIX}.actions.acknowledge`)}
                    </button>
                ) : (
                    <span className="text-xs text-gray-400">—</span>
                ),
            }
        })
    }, [items, i18n, acknowledgeMutation.isLoading, warehouse_id])

    const columns = useMemo(
        () => [
            {
                Header: i18n.t(`${T_PREFIX}.columns.deactivatedAt`),
                accessor: "createdAt",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.carrier`),
                accessor: "carrier",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.shippingMethod`),
                accessor: "shippingMethod",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.zipCode`),
                accessor: "zipCode",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.order`),
                accessor: "order",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.message`),
                accessor: "message",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.status`),
                accessor: "status",
                disableSortBy: true,
            },
            {
                Header: i18n.t(`${T_PREFIX}.columns.actions`),
                accessor: "actions",
                disableSortBy: true,
            },
        ],
        [i18n]
    )

    const filtersAreDefault =
        filters.onlyPending && !filters.carrier && !filters.dateFrom && !filters.dateTo

    const emptyText = filtersAreDefault
        ? warehouse_id
            ? i18n.t(`${T_PREFIX}.empty.defaultPerWarehouse`)
            : i18n.t(`${T_PREFIX}.empty.default`)
        : i18n.t(`${T_PREFIX}.empty.withFilters`)

    const paginationMeta = {
        current_page: meta.current_page,
        total_pages: meta.total_pages,
        total_count: meta.total_count,
        per_page: parseInt(pageSize, 10) || 25,
    }

    if (!hasAccess) {
        return (
            <PageView>
                <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t(`${T_PREFIX}.errors.noAccess`)}</p>
                </div>
            </PageView>
        )
    }

    return (
        <>
            <Notification
                show={notification.show}
                setShow={(val) => setNotification((prev) => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold flex items-center gap-2">
                            {i18n.t(`${T_PREFIX}.title`)}
                            {warehouse_id && (
                                <Tooltip title={i18n.t(`${T_PREFIX}.scopeTooltip`)}>
                                    <InformationCircleIcon className="h-5 w-5 text-gray-400 cursor-help" />
                                </Tooltip>
                            )}
                        </div>
                    </PageTopBar>
                }
                childrenFullWidth={true}
                topMenuFullWidth={true}
            >
                {/* Filters bar */}
                <div className="bg-white shadow rounded-lg p-4 mx-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {i18n.t(`${T_PREFIX}.filters.carrier`)}
                            </label>
                            <SelectInput
                                options={carrierOptions}
                                value={selectedCarrierOption}
                                selectedInitial={selectedCarrierOption}
                                onChange={(opt) =>
                                    handleFilterChange({
                                        carrier: opt?.id === "__all__" ? null : opt?.id || null,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {i18n.t(`${T_PREFIX}.filters.dateRange`)}
                            </label>
                            <RangePicker
                                value={dateRangeValue}
                                onChange={handleDateRangeChange}
                                format="YYYY-MM-DD"
                                className="w-full"
                                size="middle"
                                placeholder={[
                                    i18n.t(`${T_PREFIX}.filters.dateFrom`),
                                    i18n.t(`${T_PREFIX}.filters.dateTo`),
                                ]}
                            />
                        </div>
                        <div className="flex items-center pb-2">
                            <Checkbox
                                label={i18n.t(`${T_PREFIX}.filters.onlyPending`)}
                                checked={filters.onlyPending}
                                onChange={(checked) =>
                                    handleFilterChange({ onlyPending: checked })
                                }
                            />
                        </div>
                    </div>
                </div>

                {!isPreviousData && isLoading ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500 mb-4">
                            {error?.message || i18n.t(`${T_PREFIX}.errors.fetchError`)}
                        </p>
                        <Button onClick={() => refetch()}>
                            {i18n.t(`${T_PREFIX}.errors.retry`)}
                        </Button>
                    </div>
                ) : (
                    <DoubleScroll>
                        <NewTable
                            data={tableData}
                            columns={columns}
                            showLoader={isFetching}
                            showPaginationOnFooter
                            isFetching={isFetching}
                            emptyTableText={emptyText}
                            paginationMeta={paginationMeta}
                            onPaginationChange={handlePaginationChange}
                            onPageSizeChange={handlePageSizeChange}
                            showFiltersHeader={false}
                            cursorPointer={true}
                            rowProps={(row) => ({
                                onClick: () => handleRowClick(row.original.object),
                            })}
                            hasExport={false}
                            footerText={i18n.t(`${T_PREFIX}.footerText`)}
                        />
                    </DoubleScroll>
                )}
            </PageView>

            {selectedSnapshot && (
                <DeactivationDetailDrawer
                    deactivation={selectedSnapshot}
                    onClose={() => setSelectedSnapshot(null)}
                    onAcknowledge={handleAcknowledge}
                    isAcknowledging={acknowledgeMutation.isLoading}
                    referrer={location.pathname}
                />
            )}
        </>
    )
}
